import { type KeyboardEvent, useRef } from "react";
import { cx } from "./cx";
import { Icon, type IconName } from "./Icon";

interface SegOption<T extends string> {
  value: T;
  label: string;
  icon?: IconName;
  /** A quiet number after the words: "All 4". */
  count?: number;
}

/**
 * A segmented control (`.seg`): one of a few choices, as a radio group. Arrow keys move the choice, as a native
 * radio group does, and only the chosen button is in the tab order.
 */
export function Seg<T extends string>({ options, value, onChange, label, className }: {
  options: ReadonlyArray<SegOption<T>>;
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const move = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const at = Math.max(0, options.findIndex((o) => o.value === value));
    const next = options[(at + step + options.length) % options.length];
    onChange(next.value);
    box.current?.querySelector<HTMLButtonElement>(`[data-value="${CSS.escape(next.value)}"]`)?.focus();
  };
  return (
    <div ref={box} className={cx("seg", className)} role="radiogroup" aria-label={label} onKeyDown={move}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            data-value={o.value}
            className={cx(on && "on")}
            onClick={() => onChange(o.value)}
          >
            {o.icon ? <Icon name={o.icon} /> : null}
            {o.label}
            {o.count !== undefined ? <b>{o.count}</b> : null}
          </button>
        );
      })}
    </div>
  );
}
